import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FacilityService } from '../../services/facility.service';
import { RelayAccessService } from '../../services/relay-access.service';
import { Facility } from '../../models/facility.model';
import {
  RelayAccessItem,
  RelayAccessNode,
} from '../../models/relay-access.model';
import { ModalService } from '../../services/modal.service';
import { FacilityFormComponent } from '../facility-form/facility-form';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';

export interface TreeNode extends Facility {
  children: TreeNode[];
  expanded: boolean;
  accessRelays?: RelayAccessItem[];
  haveAccessibleRelays?: boolean;
}

export interface TreeState {
  count: number;
  operatorOnly: boolean;
}

interface FacilityFormResult {
  name: string;
}

const DRAG_AND_DROP_ENABLED = true;
const ROOT_DROP_ENABLED = true;
const MOBILE_BREAKPOINT = 900;
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE = 25;

const SAVE_ORDER_TO_SERVER = true;

type TreeSource = Facility | RelayAccessNode;

@Component({
  selector: 'app-tree',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tree.html',
  styleUrl: './tree.css',
})
export class TreeComponent implements OnInit {
  @Output() leafNodeClick = new EventEmitter<TreeNode>();
  @Output() treeStateChange = new EventEmitter<TreeState>();

  treeNodes: TreeNode[] = [];
  isLoading = false;
  isEditMode = false;

  draggedNode: TreeNode | null = null;
  dragTargetNode: TreeNode | null = null;
  dragPosition: 'top' | 'bottom' | 'center' | 'root' | null = null;
  touchDraggingNode: TreeNode | null = null;

  isRootDropTarget = false;

  isDesktop = true;
  selectedNodeId: string | null = null;

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressStartX = 0;
  private longPressStartY = 0;
  private lastUserId: string | null = null;

  private pendingAccessRelays = new Map<string, RelayAccessItem[]>();

  constructor(
    private facilityService: FacilityService,
    private relayAccessService: RelayAccessService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    private toastService: ToastService,
  ) {
    effect(() => {
      const user = this.authService.currentUser();
      const userId = user?.sub ?? user?.username ?? null;

      if (this.lastUserId !== null && this.lastUserId !== userId) {
        this.isEditMode = false;
        this.cancelTouchDrag();
        this.draggedNode = null;
        this.dragTargetNode = null;
        this.dragPosition = null;
        this.isRootDropTarget = false;
        this.selectedNodeId = null;
        this.pendingAccessRelays.clear();
      }

      this.lastUserId = userId;
    });
  }

  ngOnInit(): void {
    this.updateViewportMode();
    this.loadFacilities();
  }

  get canEdit(): boolean {
    return this.authService.isAdmin();
  }

  private get rootNodeIds(): Set<string> {
    return new Set(this.treeNodes.map((n) => n.id));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode();
  }

  isSelected(node: TreeNode): boolean {
    return this.selectedNodeId === node.id;
  }

  isDragging(node: TreeNode): boolean {
    return (
      this.draggedNode?.id === node.id ||
      this.touchDraggingNode?.id === node.id
    );
  }

  isDropTarget(node: TreeNode): boolean {
    return (
      this.touchDraggingNode !== null && this.touchDraggingNode.id !== node.id
    );
  }

  trackByNodeId(_index: number, node: TreeNode): string {
    return node.id;
  }

  loadFacilities(): void {
    this.isLoading = true;

    const loader$: Observable<TreeSource[]> = this.authService.isAdmin()
      ? this.facilityService.getFacilities()
      : this.relayAccessService.getOperatorFacilities();

    loader$.subscribe({
      next: (data: TreeSource[]) => {
        this.treeNodes = this.buildTree(data);
        this.isLoading = false;
        this.cdr.detectChanges();

        if (this.authService.isAdmin()) {
          this.normalizeOrderIfNeeded();
        }

        this.autoSelectSingleNode();
        this.emitTreeState();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
        this.toastService.error('Не удалось загрузить список объектов');
        this.emitTreeState();
      },
    });
  }

  onToggleClick(node: TreeNode, event: Event): void {
    event.stopPropagation();

    if (this.touchDraggingNode) return;
    if (node.children.length === 0) return;

    node.expanded = !node.expanded;
  }

  onNameClick(node: TreeNode, event: Event): void {
    event.stopPropagation();

    if (this.touchDraggingNode) {
      return;
    }

    if (this.isEditMode) return;

    if (node.children.length > 0) {
      node.expanded = true;
    }

    this.selectedNodeId = node.id;
    this.leafNodeClick.emit(node);
  }

  handleNodeClick(node: TreeNode, event: Event): void {
    event.stopPropagation();

    if (this.touchDraggingNode) {
      if (this.touchDraggingNode.id !== node.id) {
        this.commitMove(this.touchDraggingNode, node);
      }
      this.cancelTouchDrag();
      return;
    }

    if (this.isEditMode) return;

    if (node.children.length > 0) {
      node.expanded = true;
    }

    this.selectedNodeId = node.id;
    this.leafNodeClick.emit(node);
  }

  toggleEditMode(): void {
    if (!this.canEdit) return;

    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      this.cancelTouchDrag();
      this.draggedNode = null;
      this.dragTargetNode = null;
      this.dragPosition = null;
      this.isRootDropTarget = false;
    }
  }

  onTouchStart(node: TreeNode, event: TouchEvent): void {
    if (!this.canEdit || !this.isEditMode) return;
    if (this.isDesktop) return;
    if (!DRAG_AND_DROP_ENABLED) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.longPressStartX = touch.clientX;
    this.longPressStartY = touch.clientY;

    this.clearLongPressTimer();

    this.longPressTimer = setTimeout(() => {
      this.touchDraggingNode = node;
      this.cdr.detectChanges();
      (navigator as any)?.vibrate?.(15);
    }, LONG_PRESS_MS);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.longPressTimer && !this.touchDraggingNode) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (this.longPressTimer && !this.touchDraggingNode) {
      const dx = Math.abs(touch.clientX - this.longPressStartX);
      const dy = Math.abs(touch.clientY - this.longPressStartY);

      if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
        this.clearLongPressTimer();
      }
    }

    if (!this.touchDraggingNode) return;

    event.preventDefault();

    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const nodeEl = el?.closest('.tree-node-content') as HTMLElement | null;
    const targetId = nodeEl?.dataset['nodeId'];

    if (!targetId || targetId === this.touchDraggingNode.id) {
      this.dragTargetNode = null;
      this.dragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const target = this.findNodeById(targetId);
    if (!target) {
      this.dragTargetNode = null;
      this.dragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const rect = nodeEl.getBoundingClientRect();
    const offset = touch.clientY - rect.top;

    let position: 'top' | 'bottom' | 'center';
    if (offset < rect.height * 0.25) {
      position = 'top';
    } else if (offset > rect.height * 0.75) {
      position = 'bottom';
    } else {
      position = 'center';
    }

    if (
      this.rootNodeIds.has(target.id) &&
      !this.rootNodeIds.has(this.touchDraggingNode.id)
    ) {
      position = 'root' as any;
    }

    this.dragTargetNode = target;
    this.dragPosition = position as any;
    this.cdr.detectChanges();
  }

  onTouchEnd(): void {
    this.clearLongPressTimer();

    if (!this.touchDraggingNode) return;

    const dragged = this.touchDraggingNode;
    const target = this.dragTargetNode;
    const position = this.dragPosition;

    this.touchDraggingNode = null;
    this.dragTargetNode = null;
    this.dragPosition = null;
    this.isRootDropTarget = false;
    this.cdr.detectChanges();

    if (!target || !position) return;
    if (dragged.id === target.id) return;

    if (position === 'root') {
      this.commitMoveToRoot(dragged);
    } else if (position === 'top' || position === 'bottom') {
      this.commitReorder(dragged, target, position);
    } else {
      this.commitMove(dragged, target);
    }
  }

  onContextMenu(event: Event): void {
    if (this.isEditMode && this.canEdit) {
      event.preventDefault();
    }
  }

  addRootNode(): void {
    if (!this.canEdit) return;

    this.modalService.open({
      component: FacilityFormComponent,
      title: 'Добавление корневого объекта',
      inputs: { facility: { name: '' } },
      onClose: (result: FacilityFormResult | undefined) => {
        if (!result || typeof result.name !== 'string' || !result.name) return;

        const name = result.name;

        this.confirmAction(
          `Вы уверены, что хотите создать новый?`,
          'Создать',
        ).subscribe((confirmed) => {
          if (!confirmed) return;

          this.facilityService
            .createFacility({ name, parentId: null })
            .pipe(
              switchMap((created) =>
                this.syncWithServer((data) =>
                  data.some((f) => f.id === created.id),
                ),
              ),
              catchError(() => {
                this.toastService.error('Не удалось создать объект');
                return of(null);
              }),
            )
            .subscribe({
              next: (data) => {
                if (data) {
                  this.toastService.success(`Объект «${name}» создан`);
                }
              },
            });
        });
      },
    });
  }

  addChildNode(node: TreeNode, event: Event): void {
    event.stopPropagation();
    if (!this.canEdit) return;

    this.modalService.open({
      component: FacilityFormComponent,
      title: `Добавление объекта в "${node.name}"`,
      inputs: { facility: { name: '' } },
      onClose: (result: FacilityFormResult | undefined) => {
        if (!result || typeof result.name !== 'string' || !result.name) return;

        const name = result.name;
        const parentId = node.id;
        const parentName = node.name;

        this.confirmAction(
          `Вы уверены, что хотите создать объект "${name}" внутри "${parentName}"?`,
          'Создать',
        ).subscribe((confirmed) => {
          if (!confirmed) return;

          this.facilityService
            .createFacility({ name, parentId })
            .pipe(
              switchMap((created) =>
                this.syncWithServer((data) => {
                  const fresh = data.find((f) => f.id === created.id);
                  return !!fresh && fresh.parentId === parentId;
                }),
              ),
              catchError(() => {
                this.toastService.error('Не удалось создать объект');
                return of(null);
              }),
            )
            .subscribe({
              next: (data) => {
                if (data) {
                  this.toastService.success(
                    `Объект «${name}» создан в «${parentName}»`,
                  );
                }
              },
            });
        });
      },
    });
  }

  editNode(node: TreeNode, event: Event): void {
    event.stopPropagation();
    if (!this.canEdit) return;

    const oldName = node.name;
    const nodeId = node.id;
    const nodeParentId = node.parentId;

    this.modalService.open({
      component: FacilityFormComponent,
      title: 'Редактирование объекта',
      inputs: { facility: { name: oldName } },
      onClose: (result: FacilityFormResult | undefined) => {
        if (!result || typeof result.name !== 'string' || !result.name) return;
        if (result.name === oldName) return;

        const newName = result.name;

        this.confirmAction(
          `Вы уверены, что хотите переименовать "${oldName}" в "${newName}"?`,
          'Сохранить',
        ).subscribe((confirmed) => {
          if (!confirmed) return;

          this.facilityService
            .updateFacility(nodeId, {
              name: newName,
              parentId: nodeParentId,
              orderNum: node.orderNum,
            })
            .pipe(
              switchMap(() =>
                this.syncWithServer((data) => {
                  const fresh = data.find((f) => f.id === nodeId);
                  return !!fresh && fresh.name === newName;
                }),
              ),
              catchError(() => {
                this.toastService.error('Не удалось сохранить изменения');
                return of(null);
              }),
            )
            .subscribe({
              next: (data) => {
                if (data) {
                  this.toastService.success(
                    `Объект переименован в «${newName}»`,
                  );
                }
              },
            });
        });
      },
    });
  }

  deleteNode(node: TreeNode, event: Event): void {
    event.stopPropagation();
    if (!this.canEdit) return;

    const deletedId = node.id;
    const deletedName = node.name;

    this.confirmAction(
      `Вы действительно хотите удалить объект "${deletedName}" и все его вложенные элементы?`,
      'Удалить',
      'btn-danger',
    ).subscribe((confirmed) => {
      if (!confirmed) return;

      this.facilityService
        .deleteFacility(deletedId)
        .pipe(
          switchMap(() =>
            this.syncWithServer(
              (data) => !data.some((f) => f.id === deletedId),
            ),
          ),
          catchError(() => {
            this.toastService.error('Не удалось удалить объект');
            return of(null);
          }),
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.toastService.success(`Объект «${deletedName}» удалён`);
            }
          },
        });
    });
  }

  onDragStart(node: TreeNode, event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED) return;
    if (!this.canEdit || !this.isEditMode) return;

    this.draggedNode = node;
    this.isRootDropTarget = false;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', node.id);
    }
  }

  onDragOver(node: TreeNode, event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED) return;
    if (
      !this.canEdit ||
      !this.isEditMode ||
      !this.draggedNode ||
      this.draggedNode.id === node.id
    )
      return;

    if (this.isDescendant(this.draggedNode, node)) return;

    event.preventDefault();
    event.stopPropagation();

    this.dragTargetNode = node;

    const isTargetRoot = this.rootNodeIds.has(node.id);
    const isDraggedRoot = this.rootNodeIds.has(this.draggedNode.id);

    if (ROOT_DROP_ENABLED && isTargetRoot && !isDraggedRoot) {
      this.dragPosition = 'root';
      this.isRootDropTarget = true;
      return;
    }

    const targetElement = (event.currentTarget as HTMLElement).closest(
      '.tree-node-content',
    );

    if (targetElement) {
      const bounding = targetElement.getBoundingClientRect();
      const offset = event.clientY - bounding.top;

      if (offset < bounding.height * 0.25) {
        this.dragPosition = 'top';
      } else if (offset > bounding.height * 0.75) {
        this.dragPosition = 'bottom';
      } else {
        this.dragPosition = 'center';
      }
    } else {
      this.dragPosition = 'center';
    }

    this.isRootDropTarget = false;
  }

  onDragLeave(node: TreeNode, _event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED) return;

    if (this.dragTargetNode?.id === node.id) {
      this.dragTargetNode = null;
      this.dragPosition = null;
      this.isRootDropTarget = false;
    }
  }

  onDrop(targetNode: TreeNode, event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED) return;
    if (!this.canEdit || !this.isEditMode || !this.draggedNode) return;

    event.preventDefault();
    event.stopPropagation();

    const dragged = this.draggedNode;
    const position = this.dragPosition;

    this.dragTargetNode = null;
    this.dragPosition = null;
    this.draggedNode = null;
    this.isRootDropTarget = false;

    if (position === 'root') {
      this.commitMoveToRoot(dragged);
      return;
    }

    if (position === 'top' || position === 'bottom') {
      this.commitReorder(dragged, targetNode, position);
      return;
    }

    this.commitMove(dragged, targetNode);
  }

  onRootDragOver(event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED || !ROOT_DROP_ENABLED) return;
    if (!this.canEdit || !this.isEditMode || !this.draggedNode) return;

    if (this.draggedNode.parentId === null) return;

    event.preventDefault();
    event.stopPropagation();

    this.dragTargetNode = null;
    this.dragPosition = 'root';
    this.isRootDropTarget = true;
  }

  onRootDragLeave(event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED || !ROOT_DROP_ENABLED) return;

    const related = event.relatedTarget as Node | null;
    const current = event.currentTarget as Node | null;

    if (related && current && current.contains(related)) return;

    this.isRootDropTarget = false;

    if (this.dragPosition === 'root') {
      this.dragPosition = null;
    }
  }

  onRootDrop(event: DragEvent): void {
    if (!DRAG_AND_DROP_ENABLED || !ROOT_DROP_ENABLED) return;
    if (!this.canEdit || !this.isEditMode || !this.draggedNode) return;

    event.preventDefault();
    event.stopPropagation();

    const dragged = this.draggedNode;

    this.dragTargetNode = null;
    this.dragPosition = null;
    this.draggedNode = null;
    this.isRootDropTarget = false;

    this.commitMoveToRoot(dragged);
  }

  onTouchDropToRoot(): void {
    const dragged = this.touchDraggingNode;
    if (!dragged) return;

    this.cancelTouchDrag();
    this.commitMoveToRoot(dragged);
  }

  private emitTreeState(): void {
    const operatorOnly =
      this.authService.isOperator() && !this.authService.isAdmin();

    this.treeStateChange.emit({
      count: operatorOnly
        ? this.countAccessibleRelayNodes(this.treeNodes)
        : this.treeNodes.length,
      operatorOnly,
    });
  }

  private countAccessibleRelayNodes(nodes: TreeNode[]): number {
    let count = 0;

    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        if (n.haveAccessibleRelays === true && n.accessRelays && n.accessRelays.length > 0) {
          count++;
        }
        if (n.children.length > 0) walk(n.children);
      }
    };

    walk(nodes);
    return count;
  }

  private autoSelectSingleNode(): void {
    if (this.authService.isAdmin()) return;

    const accessibleNodes: TreeNode[] = [];

    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        if (n.haveAccessibleRelays === true && n.accessRelays && n.accessRelays.length > 0) {
          accessibleNodes.push(n);
        }
        if (n.children.length > 0) walk(n.children);
      }
    };

    walk(this.treeNodes);

    if (accessibleNodes.length !== 1) return;

    const onlyNode = accessibleNodes[0];
    this.selectedNodeId = onlyNode.id;
    this.leafNodeClick.emit(onlyNode);
  }

  private collectAllowedIds(nodes: RelayAccessNode[]): Set<string> {
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const allowed = new Set<string>();

    const includeWithParents = (node: RelayAccessNode) => {
      if (allowed.has(node.id)) return;
      allowed.add(node.id);

      if (node.parentId) {
        const parent = nodeById.get(node.parentId);
        if (parent) includeWithParents(parent);
      }
    };

    for (const node of nodes) {
      if (node.haveAccessibleRelays) {
        includeWithParents(node);
      }
    }

    return allowed;
  }

  private resolveCurrentUserId(): string | null {
    const me = this.authService.currentUser();
    if (!me) return null;
    return me.sub ?? null;
  }

  private updateViewportMode(): void {
    this.isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
  }

  private syncWithServer(
    expectation?: (data: Facility[]) => boolean,
  ): Observable<Facility[]> {
    const expandedIds = this.collectExpandedIds(this.treeNodes);

    return this.facilityService.getFacilities().pipe(
      map((data) => {
        this.treeNodes = this.buildTree(data, expandedIds);
        this.cdr.detectChanges();

        if (expectation && !expectation(data)) {
          this.showAlertModal(
            'Данные на сервере отличаются от ожидаемых. ' +
            'Дерево приведено к состоянию сервера.',
          );
        }

        this.emitTreeState();

        return data;
      }),
      catchError(() => {
        this.showAlertModal(
          'Не удалось получить актуальные данные с сервера. ' +
          'Попробуйте обновить страницу.',
        );

        return of([]);
      }),
    );
  }

  private collectExpandedIds(nodes: TreeNode[]): Set<string> {
    const ids = new Set<string>();

    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        if (n.expanded) ids.add(n.id);
        if (n.children.length) walk(n.children);
      }
    };

    walk(nodes);
    return ids;
  }

  private buildTree<T extends TreeSource>(
    facilities: T[],
    expandedIds?: Set<string>,
  ): TreeNode[] {
    const nodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    facilities.forEach((f) => {
      const hasAccessRelays = 'relays' in f;
      const hasHaveAccessibleRelays = 'haveAccessibleRelays' in f;
      const orderNum = 'orderNum' in f ? (f.orderNum ?? null) : null;
      const createdAt = 'createdAt' in f ? f.createdAt : '';
      const updatedAt = 'updatedAt' in f ? f.updatedAt : '';

      const node: TreeNode = {
        id: f.id,
        name: f.name,
        parentId: f.parentId,
        orderNum,
        createdAt,
        updatedAt,
        children: [],
        expanded: expandedIds ? expandedIds.has(f.id) : false,
        accessRelays: hasAccessRelays ? f.relays : undefined,
        haveAccessibleRelays: hasHaveAccessibleRelays
          ? f.haveAccessibleRelays
          : undefined,
      };

      nodeMap.set(f.id, node);
    });

    facilities.forEach((f) => {
      const node = nodeMap.get(f.id)!;

      if (f.parentId) {
        const parent = nodeMap.get(f.parentId);

        if (parent) {
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return this.sortTree(roots);
  }

  private sortTree(nodes: TreeNode[]): TreeNode[] {
    const sorted = [...nodes].sort((a, b) => this.compareNodes(a, b));

    for (const node of sorted) {
      if (node.children.length > 0) {
        node.children = this.sortTree(node.children);
      }
    }

    return sorted;
  }

  private compareNodes(a: TreeNode, b: TreeNode): number {
    const aHasOrder = a.orderNum !== null && a.orderNum !== undefined;
    const bHasOrder = b.orderNum !== null && b.orderNum !== undefined;

    if (aHasOrder && bHasOrder && a.orderNum !== b.orderNum) {
      return (a.orderNum as number) - (b.orderNum as number);
    }

    if (aHasOrder !== bHasOrder) {
      return aHasOrder ? -1 : 1;
    }

    const aHasChildren = a.children.length > 0;
    const bHasChildren = b.children.length > 0;

    if (aHasChildren !== bHasChildren) {
      return aHasChildren ? -1 : 1;
    }

    return a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' });
  }

  private clearLongPressTimer(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private cancelTouchDrag(): void {
    this.clearLongPressTimer();
    this.touchDraggingNode = null;
    this.cdr.detectChanges();
  }

  private confirmAction(
    message: string,
    confirmText: string,
    buttonClass = 'btn-primary',
  ): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.modalService.open({
        component: ConfirmModalComponent,
        title: 'Подтверждение действия',
        inputs: {
          message,
          confirmText,
          confirmButtonClass: buttonClass,
        },
        onClose: (result) => {
          subscriber.next(result === true);
          subscriber.complete();
        },
      });
    });
  }

  private showAlertModal(message: string): void {
    this.modalService.open({
      component: ConfirmModalComponent,
      title: 'Внимание',
      inputs: {
        message,
        confirmText: 'Понятно',
        cancelText: 'Закрыть',
        confirmButtonClass: 'btn-primary',
      },
      onClose: () => {},
    });
  }

  private commitMove(dragged: TreeNode, targetNode: TreeNode): void {
    if (dragged.id === targetNode.id) return;
    if (this.isDescendant(dragged, targetNode)) return;

    const draggedId = dragged.id;
    const draggedName = dragged.name;
    const newParentId = targetNode.id;
    const newParentName = targetNode.name;

    this.confirmAction(
      `Вы уверены, что хотите переместить объект "${draggedName}" в "${newParentName}"?`,
      'Переместить',
    ).subscribe((confirmed) => {
      if (!confirmed) return;

      this.facilityService
        .updateFacility(draggedId, {
          name: draggedName,
          parentId: newParentId,
          orderNum: dragged.orderNum,
        })
        .pipe(
          switchMap(() =>
            this.syncWithServer((data) => {
              const fresh = data.find((f) => f.id === draggedId);
              return !!fresh && fresh.parentId === newParentId;
            }),
          ),
          catchError(() => {
            this.toastService.error('Не удалось переместить объект');
            return of(null);
          }),
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.toastService.success(
                `Объект «${draggedName}» перемещён в «${newParentName}»`,
              );
            }
          },
        });
    });
  }

  private commitMoveToRoot(dragged: TreeNode): void {
    if (dragged.parentId === null) return;

    const draggedId = dragged.id;
    const draggedName = dragged.name;

    this.confirmAction(
      `Вы уверены, что хотите вынести объект "${draggedName}" в корень?`,
      'Переместить',
    ).subscribe((confirmed) => {
      if (!confirmed) return;

      this.facilityService
        .updateFacility(draggedId, {
          name: draggedName,
          parentId: null,
          orderNum: dragged.orderNum,
        })
        .pipe(
          switchMap(() =>
            this.syncWithServer((data) => {
              const fresh = data.find((f) => f.id === draggedId);
              return !!fresh && fresh.parentId === null;
            }),
          ),
          catchError(() => {
            this.toastService.error('Не удалось переместить объект в корень');
            return of(null);
          }),
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.toastService.success(
                `Объект «${draggedName}» вынесен в корень`,
              );
            }
          },
        });
    });
  }

  private commitReorder(
    dragged: TreeNode,
    target: TreeNode,
    position: 'top' | 'bottom',
  ): void {
    if (dragged.id === target.id) return;

    if (dragged.parentId !== target.parentId) {
      this.commitMove(dragged, target);
      return;
    }

    const siblings =
      target.parentId === null
        ? this.treeNodes
        : this.findNodeById(target.parentId)?.children;

    if (!siblings) return;

    const fromIndex = siblings.findIndex((n) => n.id === dragged.id);
    const targetIndex = siblings.findIndex((n) => n.id === target.id);

    if (fromIndex === -1 || targetIndex === -1) return;
    if (fromIndex === targetIndex) return;

    const without = siblings.filter((n) => n.id !== dragged.id);
    const targetIdxInWithout = without.findIndex((n) => n.id === target.id);
    const insertAt =
      position === 'top' ? targetIdxInWithout : targetIdxInWithout + 1;

    without.splice(insertAt, 0, dragged);

    const updates: Array<{ id: string; orderNum: number }> = [];

    without.forEach((node, index) => {
      if (node.orderNum !== index) {
        node.orderNum = index;
        updates.push({ id: node.id, orderNum: index });
      }
    });

    if (target.parentId === null) {
      this.treeNodes = without;
    } else {
      const parent = this.findNodeById(target.parentId);
      if (parent) {
        parent.children = without;
      }
    }

    this.cdr.detectChanges();

    if (updates.length === 0) return;

    if (!SAVE_ORDER_TO_SERVER) {
      this.toastService.info('Порядок изменён (сохранение пока недоступно)');
      return;
    }

    const requests = updates
      .map((u) => {
        const node = this.findNodeById(u.id);
        if (!node) return null;

        return this.facilityService.updateFacility(u.id, {
          name: node.name,
          parentId: node.parentId,
          orderNum: u.orderNum,
        });
      })
      .filter((r): r is Observable<Facility> => r !== null);

    if (requests.length === 0) return;

    forkJoin(requests).subscribe({
      next: () => {
        this.toastService.success('Порядок обновлён');
        this.facilityService.getFacilities().subscribe({
          next: (data) => {
            const expandedIds = this.collectExpandedIds(this.treeNodes);
            this.treeNodes = this.buildTree(data, expandedIds);
            this.cdr.detectChanges();
            this.emitTreeState();
          },
          error: () => {},
        });
      },
      error: () => {
        this.toastService.error('Не удалось сохранить порядок');
        this.facilityService.getFacilities().subscribe({
          next: (data) => {
            const expandedIds = this.collectExpandedIds(this.treeNodes);
            this.treeNodes = this.buildTree(data, expandedIds);
            this.cdr.detectChanges();
            this.emitTreeState();
          },
          error: () => {},
        });
      },
    });
  }

  private findNodeById(
    id: string,
    nodes: TreeNode[] = this.treeNodes,
  ): TreeNode | null {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children.length > 0) {
        const found = this.findNodeById(id, node.children);
        if (found) return found;
      }
    }
    return null;
  }

  private isDescendant(
    parent: TreeNode,
    potentialDescendant: TreeNode,
  ): boolean {
    if (parent.id === potentialDescendant.id) return true;

    for (const child of parent.children) {
      if (this.isDescendant(child, potentialDescendant)) {
        return true;
      }
    }

    return false;
  }

  private normalizeOrderIfNeeded(): void {
    if (!this.hasAnyNullOrder(this.treeNodes)) return;

    const updates = this.computeNormalizationUpdates(this.treeNodes);
    if (updates.length === 0) return;

    if (!SAVE_ORDER_TO_SERVER) return;

    const requests = updates.map((u) =>
      this.facilityService.updateFacility(u.id, {
        name: u.name,
        parentId: u.parentId,
        orderNum: u.orderNum,
      }),
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.facilityService.getFacilities().subscribe({
          next: (data) => {
            const expandedIds = this.collectExpandedIds(this.treeNodes);
            this.treeNodes = this.buildTree(data, expandedIds);
            this.cdr.detectChanges();
            this.emitTreeState();
          },
          error: () => {},
        });
      },
      error: () => {},
    });
  }

  private hasAnyNullOrder(nodes: TreeNode[]): boolean {
    for (const n of nodes) {
      if (n.orderNum === null || n.orderNum === undefined) return true;
      if (n.children.length > 0 && this.hasAnyNullOrder(n.children)) return true;
    }
    return false;
  }

  private computeNormalizationUpdates(
    nodes: TreeNode[],
    acc: Array<{
      id: string;
      name: string;
      parentId: string | null;
      orderNum: number;
    }> = [],
  ): Array<{
    id: string;
    name: string;
    parentId: string | null;
    orderNum: number;
  }> {
    nodes.forEach((node, index) => {
      if (node.orderNum !== index) {
        acc.push({
          id: node.id,
          name: node.name,
          parentId: node.parentId,
          orderNum: index,
        });
        node.orderNum = index;
      }

      if (node.children.length > 0) {
        this.computeNormalizationUpdates(node.children, acc);
      }
    });

    return acc;
  }
}
